import React, { useState, useEffect } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { KatexPreviewPanel } from '../ui/KatexPreview';
import ImageUpload from '../ui/ImageUpload';
import { QUESTION_TYPES, DIFFICULTY_LEVELS, EXAM_TYPES, SUBJECTS, getTopicsForSubject } from '../../constants/questionConstants';

const QuestionForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialData = null,
  mode = 'create', // 'create', 'edit', 'draft'
  title = 'Add Question',
  submitButtonText = 'Create Question',
  isDraft = false // Whether this is for draft questions
}) => {
  const [formData, setFormData] = useState({
    text: '',
    questionImageUrl: '',
    options: [
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' },
      { text: '', isCorrect: false, imageUrl: '' }
    ],
    subject: '',
    topic: '',
    subtopic: '',
    category: '',
    difficulty: 'MEDIUM',
    tenantId: '',
    tags: [],
    examTypes: [],
    explanation: '',
    explanationImageUrl: '',
    questionType: 'MCQ_SINGLE_CORRECT'
  });

  const [tagInput, setTagInput] = useState('');
  const [examTypeInput, setExamTypeInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showImageUploads, setShowImageUploads] = useState(false);

  // Real data from API
  const [tenants, setTenants] = useState([]);

  // Dynamic data from API
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [topicOptions, setTopicOptions] = useState([]);
  const [subtopicOptions, setSubtopicOptions] = useState([]);
  const [difficultyOptions, setDifficultyOptions] = useState([]);
  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);
  const [examTypeOptions, setExamTypeOptions] = useState([]);

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadFormData();
      fetchDropdownData();
    }
  }, [isOpen, initialData]);

  const loadFormData = () => {
    if (initialData) {
      setFormData({
        ...initialData,
        options: initialData.options || [
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' }
        ]
      });
      
      // Check if question has images to show upload toggles
      const hasQuestionImage = initialData.questionImageUrl;
      const hasOptionImages = initialData.options?.some(opt => opt.imageUrl);
      const hasExplanationImage = initialData.explanationImageUrl;
      
      if (hasQuestionImage || hasOptionImages || hasExplanationImage) {
        setShowImageUploads(true);
      }
    } else {
      // Reset form for new question
      setFormData({
        text: '',
        questionImageUrl: '',
        options: [
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' },
          { text: '', isCorrect: false, imageUrl: '' }
        ],
        subject: '',
        topic: '',
        subtopic: '',
        category: '',
        difficulty: 'MEDIUM',
        tenantId: '',
        tags: [],
        examTypes: [],
        explanation: '',
        explanationImageUrl: '',
        questionType: 'MCQ_SINGLE_CORRECT'
      });
      setShowImageUploads(false);
    }
  };

  const fetchDropdownData = async () => {
    setLoading(true);
    try {
      if (isDraft) {
        // Use content generator endpoints for draft questions
        const [
          subjectsResponse,
          difficultyResponse,
          questionTypesResponse,
          examTypesResponse
        ] = await Promise.all([
          superAdminAPI.getContentGeneratorQuestionSubjects(),
          superAdminAPI.getDifficultyLevels(),
          superAdminAPI.getQuestionTypes(),
          superAdminAPI.getExamTypes()
        ]);

        setSubjectOptions(subjectsResponse.data || []);
        setDifficultyOptions(difficultyResponse.data || []);
        setQuestionTypeOptions(questionTypesResponse.data || []);
        setExamTypeOptions(examTypesResponse.data || []);

        // Content generators don't need tenant selection
        setTenants([]);
      } else {
        // Use super admin endpoints for published questions
        const [
          tenantsResponse,
          subjectsResponse,
          difficultyResponse,
          questionTypesResponse,
          examTypesResponse
        ] = await Promise.all([
          superAdminAPI.getAllTenants({ size: 100 }),
          superAdminAPI.getSubjects(),
          superAdminAPI.getDifficultyLevels(),
          superAdminAPI.getQuestionTypes(),
          superAdminAPI.getExamTypes()
        ]);

        if (tenantsResponse.data?.content) {
          setTenants(tenantsResponse.data.content);
        }

        setSubjectOptions(subjectsResponse.data || []);
        setDifficultyOptions(difficultyResponse.data || []);
        setQuestionTypeOptions(questionTypesResponse.data || []);
        setExamTypeOptions(examTypesResponse.data || []);
      }
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load topics when subject changes
  useEffect(() => {
    if (formData.subject) {
      loadTopics(formData.subject);
    } else {
      setTopicOptions([]);
      setSubtopicOptions([]);
    }
  }, [formData.subject]);

  // Load subtopics when topic changes
  useEffect(() => {
    if (formData.subject && formData.topic) {
      loadSubtopics(formData.subject, formData.topic);
    } else {
      setSubtopicOptions([]);
    }
  }, [formData.subject, formData.topic]);

  const loadTopics = async (subject) => {
    try {
      const response = isDraft
        ? await superAdminAPI.getContentGeneratorQuestionTopics(subject)
        : await superAdminAPI.getTopics(subject);
      setTopicOptions(response.data || []);
    } catch (error) {
      console.error('Error loading topics:', error);
      setTopicOptions([]);
    }
  };

  const loadSubtopics = async (subject, topic) => {
    try {
      const response = isDraft
        ? await superAdminAPI.getContentGeneratorQuestionSubtopics(subject, topic)
        : await superAdminAPI.getSubtopics(subject, topic);
      setSubtopicOptions(response.data || []);
    } catch (error) {
      console.error('Error loading subtopics:', error);
      setSubtopicOptions([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { text: '', isCorrect: false, imageUrl: '' }]
    }));
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addExamType = () => {
    if (examTypeInput.trim() && !formData.examTypes.includes(examTypeInput.trim())) {
      setFormData(prev => ({
        ...prev,
        examTypes: [...prev.examTypes, examTypeInput.trim()]
      }));
      setExamTypeInput('');
    }
  };

  const removeExamType = (examTypeToRemove) => {
    setFormData(prev => ({
      ...prev,
      examTypes: prev.examTypes.filter(examType => examType !== examTypeToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate form
      if (!formData.text.trim()) {
        throw new Error('Question text is required');
      }

      if (formData.questionType === 'MCQ_SINGLE_CORRECT' || formData.questionType === 'MCQ_MULTIPLE_CORRECT') {
        if (formData.options.length < 2) {
          throw new Error('At least 2 options are required');
        }
        
        const hasCorrectAnswer = formData.options.some(opt => opt.isCorrect);
        if (!hasCorrectAnswer) {
          throw new Error('At least one option must be marked as correct');
        }
      }

      // Prepare submission data
      const submissionData = {
        ...formData,
        options: formData.options.filter(opt => opt.text.trim() !== '')
      };

      await onSubmit(submissionData);
      onClose();
    } catch (error) {
      console.error('Error submitting question:', error);
      // Error handling should be done by parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex h-full max-h-[calc(90vh-80px)]">
          {/* Form Section */}
          <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Type *
                </label>
                <select
                  value={formData.questionType}
                  onChange={(e) => handleInputChange('questionType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {questionTypeOptions.map(type => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Text *
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e) => handleInputChange('text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  required
                  placeholder="Enter the question text (supports LaTeX)"
                />
              </div>

              {/* Image Upload Toggle */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="showImageUploads"
                  checked={showImageUploads}
                  onChange={(e) => setShowImageUploads(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="showImageUploads" className="text-sm font-medium text-gray-700">
                  Enable image uploads
                </label>
              </div>

              {/* Question Image Upload */}
              {showImageUploads && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Image
                  </label>
                  <ImageUpload
                    currentImageUrl={formData.questionImageUrl}
                    onImageUpload={(url) => handleInputChange('questionImageUrl', url)}
                    onImageRemove={() => handleInputChange('questionImageUrl', '')}
                  />
                </div>
              )}

              {/* Options for MCQ questions */}
              {(formData.questionType === 'MCQ_SINGLE_CORRECT' || formData.questionType === 'MCQ_MULTIPLE_CORRECT') && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Options *
                    </label>
                    <button
                      type="button"
                      onClick={addOption}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Option
                    </button>
                  </div>
                  <div className="space-y-3">
                    {formData.options.map((option, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 border border-gray-200 rounded-md">
                        <input
                          type={formData.questionType === 'MCQ_SINGLE_CORRECT' ? 'radio' : 'checkbox'}
                          name="correctAnswer"
                          checked={option.isCorrect}
                          onChange={(e) => {
                            if (formData.questionType === 'MCQ_SINGLE_CORRECT') {
                              // For single correct, uncheck all others
                              const newOptions = formData.options.map((opt, i) => ({
                                ...opt,
                                isCorrect: i === index
                              }));
                              setFormData(prev => ({ ...prev, options: newOptions }));
                            } else {
                              // For multiple correct, toggle this option
                              handleOptionChange(index, 'isCorrect', e.target.checked);
                            }
                          }}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <textarea
                            value={option.text}
                            onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            rows={2}
                            placeholder={`Option ${index + 1} (supports LaTeX)`}
                          />
                          {showImageUploads && (
                            <div className="mt-2">
                              <ImageUpload
                                currentImageUrl={option.imageUrl}
                                onImageUpload={(url) => handleOptionChange(index, 'imageUrl', url)}
                                onImageRemove={() => handleOptionChange(index, 'imageUrl', '')}
                                compact={true}
                              />
                            </div>
                          )}
                        </div>
                        {formData.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeOption(index)}
                            className="text-red-600 hover:text-red-800 mt-1"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Subject, Topic, Subtopic */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <select
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Subject</option>
                    {subjectOptions.map(subject => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topic
                  </label>
                  <select
                    value={formData.topic}
                    onChange={(e) => handleInputChange('topic', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!formData.subject}
                  >
                    <option value="">Select Topic</option>
                    {topicOptions.map(topic => (
                      <option key={topic} value={topic}>{topic}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subtopic
                  </label>
                  <select
                    value={formData.subtopic}
                    onChange={(e) => handleInputChange('subtopic', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!formData.topic}
                  >
                    <option value="">Select Subtopic</option>
                    {subtopicOptions.map(subtopic => (
                      <option key={subtopic} value={subtopic}>{subtopic}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Difficulty and Tenant */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty Level *
                  </label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {difficultyOptions.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
                {!isDraft && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tenant
                    </label>
                    <select
                      value={formData.tenantId}
                      onChange={(e) => handleInputChange('tenantId', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Cross-Tenant (All Tenants)</option>
                      {tenants.map(tenant => (
                        <option key={tenant.id} value={tenant.id}>
                          {tenant.name} ({tenant.code})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add a tag"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Exam Types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exam Types
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.examTypes.map((examType, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      {examType}
                      <button
                        type="button"
                        onClick={() => removeExamType(examType)}
                        className="ml-1 text-green-600 hover:text-green-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <select
                    value={examTypeInput}
                    onChange={(e) => setExamTypeInput(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select or type exam type</option>
                    {examTypeOptions.map(examType => (
                      <option key={examType} value={examType}>{examType}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    value={examTypeInput}
                    onChange={(e) => setExamTypeInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExamType())}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Or type custom exam type"
                  />
                  <button
                    type="button"
                    onClick={addExamType}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Explanation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Explanation
                </label>
                <textarea
                  value={formData.explanation}
                  onChange={(e) => handleInputChange('explanation', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={4}
                  placeholder="Provide an explanation for the answer (supports LaTeX)"
                />
                {showImageUploads && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Explanation Image
                    </label>
                    <ImageUpload
                      currentImageUrl={formData.explanationImageUrl}
                      onImageUpload={(url) => handleInputChange('explanationImageUrl', url)}
                      onImageRemove={() => handleInputChange('explanationImageUrl', '')}
                    />
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : submitButtonText}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="w-1/3 border-l bg-gray-50">
            <KatexPreviewPanel
              questionText={formData.text}
              questionImageUrl={formData.questionImageUrl}
              options={formData.options}
              explanation={formData.explanation}
              explanationImageUrl={formData.explanationImageUrl}
              questionType={formData.questionType}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionForm;
